import React, { useState } from 'react'
import { Platform } from 'react-native'

import { BottomSheet, colors as zetColors } from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import { B5, SH3 } from '@zet/design-system/index'
import { Stack, YStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import BrandBenifitsContent from '@zet/feature-brands/screens/brandBenefits/brandBenifitsContent'
import { i18n } from '@zet/localization'
import BrandBenifits from '@zet/svg/brandBenifits'
import EligibilityCreteria from '@zet/svg/eligibilityCreteria'
import { DEVICE_HEIGHT } from '@zet/utils/Constants'
import { getRecommendationReason } from '@zet/utils/recommendationReason'
import { getName, titleCase } from '@zet/utils/stringConversion'
import { ScrollView } from 'react-native-gesture-handler'

import { useBrandDetailsById } from '../../api/brands/hooks'
import { EligibilityCriteria } from '../../screens/eligibilityCriteria'
import Footer from './footer'
import Header from './header'

const BrandDetailsBottomSheet = ({
  snapPoints,
  isBottomSheetVisible,
  setIsBottomSheetVisible,
  navigate,
  brandId,
  isEligible,
  reason,
  criteria,
  showShareButton = true,
  viewBrandDetailsButton = true,
  sheetName,
  feature,
  tabBarPressEventName,
  alreadyApplied,
  isSourceWeb,
  isInEligible,
  customerId,
  index,
  customerPhoneNumber,
  leadAdded,
  customerName,
  variantId,
  variantName,
  setIsExploreOtherCardsBSVisible,
  eligibleCardsCountExcludingMagnet,
}: {
  isBottomSheetVisible: boolean
  eligibleCardsCountExcludingMagnet?: number
  setIsBottomSheetVisible: React.Dispatch<React.SetStateAction<boolean>>
  setIsExploreOtherCardsBSVisible?: React.Dispatch<
    React.SetStateAction<boolean>
  >
  navigate: unknown
  brandId: string | number
  isEligible?: boolean
  reason?: string
  criteria?: string
  showShareButton?: boolean
  viewBrandDetailsButton?: boolean
  sheetName?: string
  feature?: string
  tabBarPressEventName?: string
  alreadyApplied?: boolean
  isSourceWeb?: boolean
  isInEligible?: boolean
  customerId?: number
  index: number
  customerPhoneNumber?: number | string
  leadAdded?: boolean
  customerName?: string
  variantId?: string | number
  variantName?: string
  snapPoints?: any
}) => {
  const { data: content } = useBrandDetailsById(brandId)
  const brandName = content?.name || ''
  const isWeb = Platform.OS === 'web'
  const topBar = [
    {
      id: 'bar-1',
      name:
        Boolean(variantId) && content?.categoryId === 15
          ? 'customerCategories.brandBenefitsNew'
          : 'customerCategories.brandBenefitsV2',
      icon: <BrandBenifits isIconSmall />,
      isActive: true,
      renderTab: (
        <BrandBenifitsContent
          brandId={brandId}
          isFixedHeight={false}
          variantId={variantId}
        />
      ),
    },
    {
      id: 'bar-2',
      name: 'customerCategories.eligibilityCriteria',
      icon: <EligibilityCreteria isIconSmall />,
      isActive: false,
      renderTab: (
        <EligibilityCriteria partnerId={brandId} variantId={variantId} />
      ),
    },
  ]

  const [isActiveBars, setIsActiveBars] = useState(topBar)

  // ? Hnalde active tab of bottom sheet
  const handlePress = (idx: string) => {
    setIsActiveBars([
      ...isActiveBars.map((value) => {
        return { ...value, isActive: value.id === idx }
      }),
    ])
  }

  return (
    <BottomSheet
      visible={isBottomSheetVisible}
      disableBottomSheet={() => setIsBottomSheetVisible(false)}
      bodyStyle={{ maxHeight: '80%' }}
      sheetName={sheetName}
      feature={feature}
      snapPoints={
        snapPoints || (isWeb ? [DEVICE_HEIGHT - 200, 600] : undefined)
      }
      header={
        isWeb && (
          <Header
            content={content}
            variantName={variantName}
            setIsBottomSheetVisible={setIsBottomSheetVisible}
          />
        )
      }
      footer={
        isWeb && (
          <Footer
            criteria={criteria}
            setIsBottomSheetVisible={setIsBottomSheetVisible}
            customerData={{
              id: customerId,
              name: getName(customerName),
              phoneNumber: customerPhoneNumber,
            }}
            isEligible={isEligible}
            navigate={navigate}
            brandId={brandId}
            brandName={brandName}
            alreadyApplied={alreadyApplied}
            isSourceWeb={isSourceWeb}
            isInEligible={isInEligible}
            index={index}
            leadAdded={leadAdded}
            setIsExploreOtherCardsBSVisible={setIsExploreOtherCardsBSVisible}
            eligibleCardsCountExcludingMagnet={
              eligibleCardsCountExcludingMagnet
            }
            variantId={variantId}
          />
        )
      }
    >
      <YStack className="flex-1">
        {/* //? Heading for bottom Sheet */}
        {!isWeb && (
          <Header
            content={content}
            setIsBottomSheetVisible={setIsBottomSheetVisible}
          />
        )}
        {/* //? Scrollable view of bottomsheet */}
        <ScrollView className="flex-1">
          <YStack>
            {/* //? Scrolled top bar for bottom Sheet */}
            <ScrollView
              className="border-grays-30 mr-2 border-t bg-white px-3"
              horizontal={true}
              showsHorizontalScrollIndicator={false}
            >
              {isActiveBars.map((value) => (
                <Pressable
                  key={value.id}
                  className="active:bg-grays-10 rounded-lg"
                  onPress={() => handlePress(value.id)}
                  eventName="click_action"
                  eventPayload={{
                    item_name: tabBarPressEventName
                      ? tabBarPressEventName
                      : 'recommendation_app_flow_selected_tab',
                    filter_name: value.name.replace('\n', ''),
                  }}
                >
                  <SH3
                    className={`${
                      value.isActive && 'border-primary-100 border-b-2'
                    } w-100  ${
                      value.isActive ? 'text-grays-80' : 'text-grays-60'
                    } p-3 `}
                    numberOfLines={1}
                  >
                    {i18n.t(value.name)}
                  </SH3>
                </Pressable>
              ))}
            </ScrollView>
            {/* <Stack className="bg-grays-20 h-1"></Stack> */}
            {/* // ? Render main view of active tab */}
            <Stack className="bg-grays-20 mt-2">
              {isActiveBars.find((value) => value.isActive)?.renderTab}
            </Stack>
          </YStack>
        </ScrollView>
        {/* // ? Bottom View of sheet */}
        <YStack>
          {/* // ? Information section of agent either eligible or ineligible */}
          {isEligible && !isWeb ? (
            <Stack>
              <Alert
                intent={isEligible ? 'success' : 'error'}
                iconLeft={{
                  name: isEligible ? 'DoneOutlined' : 'InEligible',
                  color: isEligible
                    ? zetColors.success[150]
                    : zetColors.error[150],
                }}
                className="px-3"
              >
                <B5
                  className={isEligible ? 'text-success-200' : 'text-error-200'}
                >
                  {!isEligible && reason
                    ? criteria
                      ? getRecommendationReason(
                          criteria,
                          reason,
                          titleCase(customerName),
                          brandName
                        )
                      : reason
                    : i18n.t('recommendationBottomSheet.alertMessageEligible', {
                        name: getName(customerName),
                        brandName,
                      })}
                </B5>
              </Alert>
            </Stack>
          ) : (
            <></>
          )}
          {/* // ? Share to active customer button */}
          {!isWeb && (
            <Footer
              criteria={criteria}
              setIsBottomSheetVisible={setIsBottomSheetVisible}
              customerData={{
                id: customerId,
                name: getName(customerName),
                phoneNumber: customerPhoneNumber,
              }}
              isEligible={isEligible}
              navigate={navigate}
              brandId={brandId}
              brandName={brandName}
              alreadyApplied={alreadyApplied}
              isSourceWeb={isSourceWeb}
              isInEligible={isInEligible}
              index={index}
              leadAdded={leadAdded}
              setIsExploreOtherCardsBSVisible={setIsExploreOtherCardsBSVisible}
              eligibleCardsCountExcludingMagnet={
                eligibleCardsCountExcludingMagnet
              }
              variantId={variantId}
            />
          )}
        </YStack>
      </YStack>
    </BottomSheet>
  )
}

export default BrandDetailsBottomSheet
